import { useEffect, useMemo, useState } from "react";

// Shared selection state for the exercise/lesson picker dialogs: tracks which
// library items are selected (keyed by id), resets when the dialog (re)opens,
// and exposes the ids to render as checked (already-on-template ∪ newly-picked).
export function usePickerSelection<Raw, Item>(
  open: boolean,
  existingIds: number[],
  getId: (raw: Raw) => number,
  buildItem: (raw: Raw) => Item
) {
  const [selected, setSelected] = useState<Record<number, Item>>({});
  const [previewId, setPreviewId] = useState<number | null>(null);

  useEffect(() => {
    if (open) {
      setSelected({});
      setPreviewId(null);
    }
  }, [open]);

  const existing = useMemo(() => new Set(existingIds), [existingIds]);
  const checkedIds = useMemo(() => [...existingIds, ...Object.keys(selected).map(Number)], [existingIds, selected]);

  const toggle = (raw: Raw) => {
    const id = getId(raw);
    if (existing.has(id)) return; // already on the template
    setSelected(prev => {
      const next = { ...prev };
      if (next[id]) delete next[id];
      else next[id] = buildItem(raw);
      return next;
    });
  };

  return {
    previewId,
    setPreviewId,
    checkedIds,
    toggle,
    count: Object.keys(selected).length,
    selectedItems: (): Item[] => Object.values(selected),
  };
}
